import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useParams } from "react-router-dom";
import leadersData from "@/data/leaders.json";
import {
  ArrowLeft,
  Mail,
  Phone,
  User,
  GraduationCap,
  Award,
  Target,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const Leaders = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const leaders = leadersData.leaders;

  // If no ID is provided, show all leaders
  if (!id) {
    return (
      <Layout>
        <main className="min-h-screen">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 py-6">
            <div className="container mx-auto px-4">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 animate-fade-in">
                  The Distinguished Faculty
                </h1>
                <p className="text-lg text-muted-foreground animate-fade-in">
                  Transforming Leaders. Empowering Organizations. Impacting
                  Society.
                </p>
              </div>
            </div>
          </section>

          {/* Leaders Grid */}
          <section className="py-6">
            <div className="container mx-auto px-2 md:px-4">
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-8">
                {leaders.map((leader, index) => (
                  <Card
                    key={leader.id}
                    className="hover-scale cursor-pointer group border-muted/20 hover:border-primary/20 sm:border-primary/20 transition-all duration-300"
                    onClick={() => navigate(`/leaders/${leader.id}`)}
                  >
                    <CardContent className="p-2 md:p-6">
                      <div className="text-center">
                        <div className="w-24 h-24 md:w-48 md:h-48 mx-auto mb-2 md:mb-4 rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                          {leader.image ? (
                            <img
                              src={leader.image}
                              className="w-24 h-24 md:w-48 md:h-48 rounded-full object-fit"
                            />
                          ) : (
                            <User className="w-8 h-8 md:w-16 md:h-16 text-primary" />
                          )}
                        </div>
                        <h3 className="text- 2lg md:text-xl font-semibold text-foreground mb-2">
                          {leader.name}
                        </h3>
                        <p className="text-sm text-blue-400 mb-4 line-clamp-2">
                          {leader.title}
                        </p>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                          {leader.bio.substring(0, 150)}...
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                        >
                          View Full Profile
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </main>
      </Layout>
    );
  }

  // Find the specific leader
  const leader = leaders.find((l) => l.id === id);

  if (!leader) {
    return (
      <Layout>
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Leader Not Found
            </h1>
            <Button onClick={() => navigate("/leaders")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Leaders
            </Button>
          </div>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="min-h-screen">
        {/* Navigation */}
        <section className="py-8 border-b border-muted/20">
          <div className="container mx-auto px-4">
            <Button
              onClick={() => navigate("/leaders")}
              variant="ghost"
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Leaders
            </Button>
          </div>
        </section>

        {/* Leader Profile */}
        <section className="pt-2 pb-6">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-8 sm:mb-12">
                <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                  {leader.image ? (
                    <img
                      src={leader.image}
                      className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-20 h-20 sm:w-24 sm:h-24 text-primary" />
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 animate-fade-in">
                  {leader.name}
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-primary mb-4 sm:mb-6 animate-fade-in">
                  {leader.title}
                </p>
              </div>

              {/* Bio */}
              <Card className="mb-6 sm:mb-8">
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-primary" />
                    Biography
                  </h2>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {leader.bio}
                  </p>
                </CardContent>
              </Card>

              {/* Education */}
              {leader.education && (
                <Card className="mb-6 sm:mb-8">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                      <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-primary" />
                      Education
                    </h2>
                    <ul className="space-y-2">
                      {Array.isArray(leader.education) &&
                        leader.education.map((edu, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm sm:text-base text-muted-foreground">
                              {edu}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Certifications */}
              {leader.certifications && (
                <Card className="mb-6 sm:mb-8">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
                      <Award className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-primary" />
                      Certifications
                    </h2>
                    <ul className="space-y-2">
                      {Array.isArray(leader.certifications) &&
                        leader.certifications.map((cert, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm sm:text-base text-muted-foreground">
                              {cert}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Experience */}
              {leader.experience && (
                <Card className="mb-6 sm:mb-8">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4">
                      Professional Experience
                    </h2>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {leader.experience}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Philosophy */}
              {leader.philosophy && (
                <Card className="mb-6 sm:mb-8">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4">
                      Philosophy & Approach
                    </h2>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {leader.philosophy}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Roles / Commitment */}
              {leader.roles && (
                <Card className="mb-6 sm:mb-8">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4">
                      Commitment
                    </h2>
                    <ul className="space-y-2">
                      {Array.isArray(leader.roles) &&
                        leader.roles.map((role, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm sm:text-base text-muted-foreground">
                              {role}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Impact */}
              {leader.impact && (
                <Card className="mb-6 sm:mb-8">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-3 sm:mb-4">
                      Impact & Recognition
                    </h2>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {leader.impact}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Areas of Expertise */}
              <Card className="mb-10 sm:mb-12">
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground mb-4 sm:mb-6 flex items-center">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-primary" />
                    Areas of Expertise
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
                    {Array.isArray(leader.areasOfExpertise) &&
                      leader.areasOfExpertise.map((area, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 justify-start hover-scale hover:bg-white border-blue-200"
                        >
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-primary" />
                          {area}
                        </Badge>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default Leaders;
